import * as React from "react";
import { useClickAway, useToggle } from "react-use";
import { LogOut, Settings } from "lucide-react";
import * as analytics from "@/services/analytics";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { EmailChangeDialog } from "@/components/modals/email-change/EmailChangeDialog";
import { ProfileEditModal } from "@/components/modals/ProfileEditModal";
import { UserAvatar } from "@/components/ui/userAvatar";

export const ProfileMenu: React.FC = () => {
  const [profileMenuOpen, toggleProfileMenuOpen] = useToggle(false);
  const [profileEditOpen, toggleProfileEditOpen] = useToggle(false);
  const [emailChangeOpen, toggleEmailChangeOpen] = useToggle(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const user = useAuthenticatedUser();

  useClickAway(menuRef, () => {
    if (profileMenuOpen) {
      toggleProfileMenuOpen(false);
    }
  });

  const handleLogout = () => {
    analytics.logout();
  };

  const handleOpenProfileEdit = () => {
    toggleProfileEditOpen(true);
    toggleProfileMenuOpen(false);
  };

  const handleOpenEmailChange = () => {
    toggleProfileEditOpen(false);
    toggleEmailChangeOpen(true);
  };

  return (
    <div className="relative mt-4" ref={menuRef}>
      <button
        type="button"
        aria-label="Open profile menu"
        aria-expanded={profileMenuOpen}
        aria-controls="profile-menu-actions"
        className="flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-left transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
        onClick={() => toggleProfileMenuOpen()}
      >
        <UserAvatar user={user} size="xs" />
        <div className="text-atriablue-500 text-sm font-semibold">
          {user.name} {user.surname}
        </div>
      </button>
      {profileMenuOpen ? (
        <div
          id="profile-menu-actions"
          className="absolute -top-28 right-0 z-50 mb-2 w-full rounded-md bg-white p-2 shadow-lg"
        >
          <button
            type="button"
            onClick={handleOpenProfileEdit}
            className="text-atriablue-500 flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-left text-sm font-semibold transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
          >
            <Settings size={18} />
            <span>Edit Profile</span>
          </button>
          <a
            href="/pf-auth/logout"
            onClick={handleLogout}
            className="text-atriablue-500 flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-left text-sm font-semibold transition-colors duration-200 ease-in-out hover:bg-white hover:text-gray-600"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </a>
        </div>
      ) : null}
      <ProfileEditModal
        open={profileEditOpen}
        onOpenChange={toggleProfileEditOpen}
        onRequestEmailChange={handleOpenEmailChange}
      />
      <EmailChangeDialog open={emailChangeOpen} onOpenChange={toggleEmailChangeOpen} />
    </div>
  );
};
